import axios from 'axios';
import { clientYardService } from './client-yard';
import { downloadBlob, uniqueArray } from '../../utils';
import { ImageObjType, User, YardGetBlocksQuery } from '../../types';
import { i18n } from '../../i18n';

export const createCompany = ({ path, props }: any) => axios.post(path, props);

export const editCompany = ({ path, props }: any) => axios.patch(path, props);

class YardCompanies {
  getAll = (data: any = {}) => axios.get('/yard-companies', { ...data }).then((res) => res.data);

  get = (data: any) => axios.get(`/yard-companies/${data.params.id}`).then((res) => res.data.data);

  uploadLogo = (id: string, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);

    return axios.patch(`/yard-companies/${id}/upload-logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  deleteLogo = (id: string, name: string) =>
    axios.patch(`/yard-companies/${id}/delete-logo`, { name });

  updateLogo = (
    id: string,
    logo?: { action: 'upload'; file: File } | { action: 'delete'; name: string },
  ) => {
    if (!logo) return Promise.resolve(undefined);
    if (logo.action === 'delete') return this.deleteLogo(id, logo.name).then(() => null);

    return this.uploadLogo(id, logo.file).then((res) => res.data.data);
  };

  downloadLogo = (href: string, name: string) =>
    axios.get(href, { responseType: 'blob' }).then((res) => downloadBlob(res.data, name));
}

class Yards {
  getAll = (data: any = {}) => axios.get(`/yards`, data).then((res) => res.data);

  getBlocks = (data: any) =>
    axios.get<YardGetBlocksQuery>(`/yards/blocks`, data).then((res) => res.data.data);

  getBlocksScene = (data: any) =>
    axios.get(`/yards/blocks-scene`, data).then((res) => res.data.data);

  get = (data: any) => axios.get(`/yards/${data.params.id}`).then((res) => res.data.data);

  options = async (data: any) => {
    if (!data.yardCompanyId) return;

    const res = await this.getAll({
      params: {
        yardCompanyId: data.yardCompanyId,
        notLoading: true,
      },
    });

    const country = uniqueArray(res.data, (obj) => obj.address.country).map((obj) => ({
      label: obj.address.country,
      id: obj.address.country,
    }));
    const city = uniqueArray(res.data, (obj) => obj.address.city).map((obj) => ({
      label: obj.address.city,
      country: obj.address.country,
      id: obj.address.city,
    }));
    const yard = res.data.map((obj: any) => ({
      label: obj.name,
      country: obj.address.country,
      city: obj.address.city,
      id: obj.id,
    }));

    return { country, city, yard };
  };

  clientsForYard = (data: any) =>
    axios
      .get(`/yards/${data.id}/clients`, {
        params: data.params,
      })
      .then((res) => res.data.data);
}

class Clients {
  getAll = (data: any = {}) =>
    axios
      .get(`/clients`, {
        ...data,
      })
      .then((res) => res.data);

  get = (props: any) => axios.get(`/clients/${props.params.id}`);

  uploadDocuments = (id: string, documents: ImageObjType[] = [], descriptions: string[]) => {
    if (!documents.length) return true;

    const formData = new FormData();
    for (const i in documents.map((obj) => obj.file)) {
      formData.append(`documents`, documents[i].file);
    }
    formData.append('descriptions', JSON.stringify(descriptions));

    return axios.patch(`/clients/${id}/upload-documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  deleteDocuments = (id: string, documents: ImageObjType[] = []) => {
    if (!documents.length) return true;

    return axios.patch(`/clients/${id}/delete-documents`, {
      names: documents.map((obj) => obj.name),
    });
  };

  updateDocuments = async ({
    id,
    documents = [],
    descriptions,
  }: {
    id: string;
    documents?: ImageObjType[];
    descriptions: string[];
  }) => {
    await this.deleteDocuments(
      id,
      documents.filter((obj) => obj.action === 'delete'),
    );

    return this.uploadDocuments(
      id,
      documents.filter((obj) => obj.action === 'upload'),
      descriptions,
    );
  };

  downloadDocument = (href: string, name: string) => {
    return axios.get(href, { responseType: 'blob' }).then((res) => {
      downloadBlob(res.data, name);
    });
  };
}

class BelongsToOptions {
  YardCompany = (companyName?: string, withoutAll?: boolean) => async () => {
    const params: any = {
      notLoading: true,
      short: true,
    };

    const res = await yardCompaniesService.getAll({
      params,
    });

    const result = res.data.map((obj: any) => ({ label: obj.legalName, id: obj.id }));
    if (!withoutAll) result.unshift({ label: i18n.t('label.all'), id: 'All' });

    return result;
  };

  Yard = async (yardCompanyId?: string, withoutAll?: boolean, returnKey?: string) => {
    const params: any = { notLoading: true };

    if (yardCompanyId && yardCompanyId !== 'All') params.yardCompanyId = yardCompanyId;

    const res = await yardsService.getAll({
      params,
    });

    const country = uniqueArray(res.data, (obj) => obj.address.country).map((obj) => ({
      label: obj.address.country,
      id: obj.address.country,
    }));
    const city = uniqueArray(res.data, (obj) => obj.address.city).map((obj) => ({
      label: obj.address.city,
      country: obj.address.country,
      id: obj.address.city,
    }));
    const yard = res.data.map((obj: any) => ({
      label: obj.name,
      country: obj.address.country,
      city: obj.address.city,
      id: obj.id,
    }));

    if (!withoutAll) {
      country.unshift({ label: i18n.t('label.all'), id: 'All' });
      city.unshift({ label: i18n.t('label.all'), country: 'All', id: 'All' });
      yard.unshift({ label: i18n.t('label.all'), id: 'All' });
    }

    const result: any = { country, city, yard };

    if (returnKey) {
      return result[returnKey];
    }

    return result;
  };

  Client = (companyName?: string, withoutAll?: boolean, showClientName?: boolean) => async () => {
    const params: any = { notLoading: true };

    const res = await clientsService.getAll({
      params,
    });

    const result = res.data.map((obj: any) => ({
      label: showClientName ? obj.legalName : obj.code,
      id: obj.id,
    }));
    if (!withoutAll) result.unshift({ label: i18n.t('label.all'), id: 'All' });

    return result;
  };
}

// this class for ADMINS
class BelongsToFullOptions {
  Yard = (clientId: string, action: string, withoutAll: boolean, user: User) => async () => {
    const params: any = { notLoading: true };

    let res = await clientYardService.getYardsForClient(
      {
        params,
        id: clientId,
      },
      user,
    );
    let result: any;

    if (action === 'add-yards') res = res.notAssignedYards;
    if (action === 'reactivate-yards') res = res.deactivated;
    if (action === 'deactivate-yards') res = res.active;

    if (action === 'add-yards') {
      result = res.map((obj: any) => ({ label: obj.name, id: obj.id }));
    } else {
      result = res.map((obj: any) => ({ label: obj.yard.name, id: obj.yard.id }));
    }
    if (!withoutAll) result.unshift({ label: i18n.t('label.all'), id: 'All' });

    return result;
  };

  Client = (yardId: string, action: string, withoutAll: boolean) => async () => {
    if (!yardId) return [];

    const params: any = { notLoading: true };

    let res = await yardsService.clientsForYard({
      params,
      id: yardId,
    });
    let result: any;

    if (action === 'add-clients') res = res.notAssigned;
    if (action === 'reactivate-clients') res = res.deactived;
    if (action === 'deactivate-clients') res = res.active;

    if (action === 'add-clients') {
      result = res.map((obj: any) => ({ label: obj.code, id: obj.id }));
    } else {
      result = res.map((obj: any) => ({ label: obj.client.code, id: obj.client.id }));
    }
    if (!withoutAll) result.unshift({ label: i18n.t('label.all'), id: 'All' });

    return result;
  };
}

export const yardCompaniesService = new YardCompanies();
export const yardsService = new Yards();
export const clientsService = new Clients();
export const belongsToOptions = new BelongsToOptions();
export const belongsToOptionsQueryKeys = {
  YardCompany: 'yard-companies-short',
  Yard: 'yard',
  Client: 'client',
};
export const belongsToFullOptions = new BelongsToFullOptions();
